import type Graph from 'graphology';
import { X } from 'lucide-react';

import { NODE_COLORS } from '../lib/graph-constants.js';
import type { SigmaEdgeAttributes, SigmaNodeAttributes } from '../lib/graph-node-attributes.js';

import { RelatedSection } from './node-source-panel-related-section.js';
import { SourceSection } from './node-source-panel-source-section.js';

interface NodeSourcePanelProps {
  readonly selectedNodeId: string | null;
  readonly graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes> | null;
  readonly slug: string;
  readonly onClose: () => void;
  readonly onNodeSelect: (nodeId: string) => void;
  /** Optional branch override forwarded to the source viewer. When undefined,
   *  the server uses the registry's `defaultBranch`. */
  readonly branch?: string;
}

export function NodeSourcePanel({
  selectedNodeId,
  graph,
  slug,
  onClose,
  onNodeSelect,
  branch,
}: NodeSourcePanelProps): JSX.Element | null {
  if (!selectedNodeId || !graph?.hasNode(selectedNodeId)) return null;

  const attr = graph.getNodeAttributes(selectedNodeId);
  const color = NODE_COLORS[attr.kind] ?? '#6b7280';

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-[5] flex max-h-[48vh] flex-col rounded-t-2xl border-t border-border-subtle bg-deep/95 shadow-panel backdrop-blur-md animate-slide-up"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-border-subtle px-4 py-2.5">
        <span
          className="rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-white"
          style={{ background: color }}
        >
          {attr.kind}
        </span>
        <span className="flex-1 truncate font-mono text-sm font-semibold text-text-primary">
          {attr.label}
        </span>
        {attr.filePath && (
          <span className="hidden truncate font-mono text-[10px] text-text-muted sm:block">
            {attr.filePath}
            {attr.startLine ? `:${attr.startLine}` : ''}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-1 rounded p-0.5 text-text-muted transition-colors hover:bg-hover hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body: source + related side by side */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <SourceSection
          slug={slug}
          attr={attr}
          selectedNodeId={selectedNodeId}
          {...(branch ? { branch } : {})}
        />
        <RelatedSection graph={graph} selectedNodeId={selectedNodeId} onNodeSelect={onNodeSelect} />
      </div>
    </div>
  );
}
